import React from "react";
import {
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AccessTime,
  AutoAwesome,
  GroupsOutlined,
} from "@mui/icons-material";

const formatSchedule = (dateString) => {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) return "Schedule pending";

  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kolkata",
    timeZoneName: "short",
  });
};

const SplitsDisplay = ({ splitsData }) => {
  if (!Array.isArray(splitsData) || splitsData.length === 0) return null;

  const totalContacts = splitsData.reduce(
    (total, split) => total + (Number(split?.count) || 0),
    0
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderRadius: 2.5,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          p: 2.5,
          bgcolor: "rgba(18, 140, 126, 0.06)",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <AutoAwesome fontSize="small" />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              AI campaign schedule
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review the generated batches before adding this audience.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`${splitsData.length} ${
              splitsData.length === 1 ? "batch" : "batches"
            }`}
            color="primary"
            variant="outlined"
          />
          <Chip
            size="small"
            icon={<GroupsOutlined />}
            label={`${totalContacts.toLocaleString("en-IN")} contacts`}
            sx={{ bgcolor: "background.paper" }}
          />
        </Stack>
      </Box>

      <Box>
        {splitsData.map((split, index) => {
          const count = Number(split?.count) || 0;

          return (
            <React.Fragment key={`${split?.startTime || "batch"}-${index}`}>
              {index > 0 && <Divider />}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "40px 1fr", sm: "40px 1fr auto" },
                  alignItems: "center",
                  columnGap: 1.5,
                  rowGap: 1,
                  p: 2.25,
                  transition: "background-color 160ms ease",
                  "&:hover": { bgcolor: "grey.50" },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700}>
                    Batch {index + 1}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ mt: 0.35, color: "text.secondary" }}
                  >
                    <AccessTime sx={{ fontSize: 16 }} />
                    <Typography variant="caption">
                      {formatSchedule(split?.startTime)}
                    </Typography>
                  </Stack>
                </Box>

                <Chip
                  size="small"
                  label={`${count.toLocaleString("en-IN")} ${
                    count === 1 ? "contact" : "contacts"
                  }`}
                  color="success"
                  variant="outlined"
                  sx={{
                    gridColumn: { xs: "2", sm: "auto" },
                    justifySelf: "start",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
};

export default SplitsDisplay;
